<template>
    <div class="workflow-compact">
        <!-- 승인 대기 섹션 -->
        <div class="section-block">
            <div class="section-header-compact">
                <div class="section-actions">
                    <CarbonSelect
                        :value="pendingFilter.targetType == null ? '' : pendingFilter.targetType"
                        :options="targetTypeFilterOptions"
                        placeholder="전체 타입"
                        :placeholderDisabled="false"
                        @input="(v) => { pendingFilter.targetType = v === '' ? null : v; filterPending(); }"
                    />
                    <CarbonInput
                        v-model="pendingFilter.searchKeyword"
                        variant="search"
                        placeholder="검색"
                        @input="filterPending"
                    />
                </div>
            </div>

            <div class="table-container-compact">
                <table class="table-compact">
                    <thead>
                        <tr>
                            <th>대상</th>
                            <th>요청자</th>
                            <th>요청일</th>
                            <th style="width: 140px">작업</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loadingPending">
                            <td colspan="4" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</td>
                        </tr>
                        <tr v-else-if="filteredPendingApprovals.length === 0">
                            <td colspan="4" class="text-center empty">승인 대기 중인 항목이 없습니다</td>
                        </tr>
                        <tr v-else v-for="approval in paginatedPending" :key="approval.approvalIdx">
                            <td>
                                <span class="tag-small" :class="getTargetClass(approval.targetType)">
                                    <i :class="getTargetIcon(approval.targetType)"></i>
                                    {{ getTargetTypeLabel(approval.targetType) }}
                                </span>
                                <div class="target-name-text">{{ approval.targetName || `ID: ${approval.targetIdx}` }}</div>
                            </td>
                            <td>{{ approval.requesterName }}</td>
                            <td>{{ formatDateTime(approval.requestDate) }}</td>
                            <td>
                                <div class="action-buttons">
                                    <button class="btn-mini btn-approve" @click="approveRequest(approval)" title="승인">
                                        <i class="fas fa-check"></i>
                                        승인
                                    </button>
                                    <button class="btn-mini btn-reject" @click="rejectRequest(approval)" title="반려">
                                        <i class="fas fa-times"></i>
                                        반려
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <TablePagination
                v-if="filteredPendingApprovals.length > 0"
                :total-items="filteredPendingApprovals.length"
                :current-page.sync="currentPendingPage"
                :page-size.sync="itemsPerPage"
            />
        </div>

        <!-- 내 요청 섹션 -->
        <div class="section-block">
            <div class="section-header-compact">
                <div class="section-actions">
                    <CarbonSelect
                        :value="requestFilter.status == null ? '' : requestFilter.status"
                        :options="requestStatusOptions"
                        placeholder="전체 상태"
                        :placeholderDisabled="false"
                        @input="(v) => { requestFilter.status = v === '' ? null : v; filterRequests(); }"
                    />
                    <CarbonButton variant="add" icon="fas fa-plus" @click="openCreateRequestModal">
                        요청 생성
                    </CarbonButton>
                </div>
            </div>

            <div class="table-container-compact">
                <table class="table-compact">
                    <thead>
                        <tr>
                            <th style="width: 80px">상태</th>
                            <th>대상</th>
                            <th>승인자</th>
                            <th>요청일</th>
                            <th>처리일</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loadingRequests">
                            <td colspan="5" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</td>
                        </tr>
                        <tr v-else-if="filteredMyRequests.length === 0">
                            <td colspan="5" class="text-center empty">요청 내역이 없습니다</td>
                        </tr>
                        <tr v-else v-for="request in paginatedRequests" :key="request.approvalIdx">
                            <td>
                                <span class="status-tag" :class="getStatusClass(request.status)">
                                    <i :class="getStatusIcon(request.status)"></i>
                                    {{ getStatusLabel(request.status) }}
                                </span>
                            </td>
                            <td>
                                <span class="tag-small" :class="getTargetClass(request.targetType)">
                                    <i :class="getTargetIcon(request.targetType)"></i>
                                    {{ getTargetTypeLabel(request.targetType) }}
                                </span>
                                <div class="target-name-text">{{ request.targetName || `ID: ${request.targetIdx}` }}</div>
                            </td>
                            <td>{{ request.approverName }}</td>
                            <td>{{ formatDateTime(request.requestDate) }}</td>
                            <td>{{ formatDateTime(request.processedDate) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <TablePagination
                v-if="filteredMyRequests.length > 0"
                :total-items="filteredMyRequests.length"
                :current-page.sync="currentRequestPage"
                :page-size.sync="itemsPerPage"
            />
        </div>

        <!-- 요청 생성 모달 -->
        <b-modal v-model="showCreateRequestModal" title="승인 요청 생성" size="sm" hide-footer>
            <div class="modal-form-compact">
                <div class="form-group-compact">
                    <label>대상 타입 *</label>
                    <CarbonSelect
                        v-model="createForm.targetType"
                        :options="targetTypeCreateOptions"
                        placeholder="선택하세요"
                        :placeholderDisabled="false"
                    />
                </div>
                <div class="form-group-compact">
                    <label>대상 ID *</label>
                    <CarbonInput v-model.number="createForm.targetIdx" type="number" />
                </div>
                <div class="form-group-compact">
                    <label>승인자 *</label>
                    <CarbonSelect
                        :value="createForm.approverUserIdx == null ? '' : createForm.approverUserIdx"
                        :options="approverSelectOptions"
                        placeholder="선택하세요"
                        :placeholderDisabled="false"
                        @input="createForm.approverUserIdx = $event === '' ? null : $event"
                    />
                </div>
            </div>
            <div class="modal-footer-compact">
                <CarbonButton @click="showCreateRequestModal = false">취소</CarbonButton>
                <CarbonButton
                    variant="primary"
                    :disabled="!canCreateRequest"
                    @click="createRequest"
                >
                    생성
                </CarbonButton>
            </div>
        </b-modal>
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "@/submodules/carbon-plugin/codes";
import TablePagination from "../common/TablePagination.vue";
import CarbonButton from "../common/CarbonButton.vue";
import CarbonInput from "../common/CarbonInput.vue";
import CarbonSelect from "../common/CarbonSelect.vue";

export default {
    name: "WorkflowManager",
    components: { TablePagination, CarbonButton, CarbonInput, CarbonSelect },
    props: {
        loginUser: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            pendingApprovals: [],
            filteredPendingApprovals: [],
            loadingPending: false,
            currentPendingPage: 1,
            itemsPerPage: 10,
            pendingFilter: {
                targetType: null,
                searchKeyword: "",
            },

            myRequests: [],
            filteredMyRequests: [],
            loadingRequests: false,
            currentRequestPage: 1,
            requestFilter: {
                status: null,
            },

            approvers: [],

            showCreateRequestModal: false,
            createForm: {
                targetType: "",
                targetIdx: null,
                approverUserIdx: null,
            },
        };
    },
    computed: {
        paginatedPending() {
            const start = (this.currentPendingPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredPendingApprovals.slice(start, end);
        },
        paginatedRequests() {
            const start = (this.currentRequestPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredMyRequests.slice(start, end);
        },
        canCreateRequest() {
            return this.createForm.targetType && this.createForm.targetIdx && this.createForm.approverUserIdx;
        },
        targetTypeFilterOptions() {
            const list = CarbonCodes.targetType.getList();
            const approvalTypes = ["activity_data", "inventory", "report"];
            return list.filter((o) => approvalTypes.includes(o.value)).length > 0
                ? list.filter((o) => approvalTypes.includes(o.value))
                : [
                      { value: "activity_data", text: "활동 데이터" },
                      { value: "inventory", text: "인벤토리" },
                      { value: "report", text: "보고서" },
                  ];
        },
        requestStatusOptions() {
            return [
                { value: "pending", text: "대기 중" },
                { value: "approved", text: "승인됨" },
                { value: "rejected", text: "반려됨" },
            ];
        },
        targetTypeCreateOptions() {
            return this.targetTypeFilterOptions;
        },
        approverSelectOptions() {
            return this.approvers.map((u) => ({
                value: u.userIdx,
                text: `${u.userName} (${u.loginId})`,
            }));
        },
    },
    async mounted() {
        await this.loadApprovers();
        await this.loadPendingApprovals();
        await this.loadMyRequests();
    },
    methods: {
        async loadApprovers() {
            try {
                const response = await carbonApi.accessControl.getUsers({});
                this.approvers = response.data.data?.users || [];
            } catch (error) {
                console.error("승인자 목록 로드 실패:", error);
            }
        },

        async loadPendingApprovals() {
            this.loadingPending = true;
            try {
                const response = await carbonApi.workflow.getApprovals({ status: "pending" });
                this.pendingApprovals = response.data.data?.approvals || [];
                this.filterPending();
            } catch (error) {
                console.error("승인 대기 목록 로드 실패:", error);
            } finally {
                this.loadingPending = false;
            }
        },

        async loadMyRequests() {
            this.loadingRequests = true;
            try {
                const response = await carbonApi.workflow.getApprovals({});
                const allApprovals = response.data.data?.approvals || [];
                this.myRequests = allApprovals.filter((a) => a.requesterUserIdx === this.loginUser?.userIdx);
                this.filterRequests();
            } catch (error) {
                console.error("내 요청 로드 실패:", error);
            } finally {
                this.loadingRequests = false;
            }
        },

        filterPending() {
            let filtered = [...this.pendingApprovals];

            if (this.pendingFilter.targetType) {
                filtered = filtered.filter((a) => a.targetType === this.pendingFilter.targetType);
            }

            if (this.pendingFilter.searchKeyword) {
                const keyword = this.pendingFilter.searchKeyword.toLowerCase();
                filtered = filtered.filter(
                    (a) =>
                        a.requesterName?.toLowerCase().includes(keyword) ||
                        a.targetName?.toLowerCase().includes(keyword)
                );
            }

            this.filteredPendingApprovals = filtered;
            this.currentPendingPage = 1;
        },

        filterRequests() {
            let filtered = [...this.myRequests];

            if (this.requestFilter.status) {
                filtered = filtered.filter((r) => r.status === this.requestFilter.status);
            }

            this.filteredMyRequests = filtered;
            this.currentRequestPage = 1;
        },

        async approveRequest(approval) {
            try {
                await carbonApi.workflow.approveRequest(approval.approvalIdx, { approvalComments: "" });
                this.$bvToast.toast("승인이 완료되었습니다.", { variant: "success", title: "승인 완료" });
                await this.loadPendingApprovals();
            } catch (error) {
                console.error("승인 처리 실패:", error);
                this.$bvToast.toast("승인 처리에 실패했습니다.", { variant: "danger", title: "승인 실패" });
            }
        },

        async rejectRequest(approval) {
            const reason = await this.$bvModal.msgBoxConfirm("반려 사유를 입력하세요", {
                title: "반려 처리",
                okTitle: "반려",
                cancelTitle: "취소",
                okVariant: "danger",
            });

            if (reason) {
                try {
                    await carbonApi.workflow.rejectRequest(approval.approvalIdx, { rejectionReason: reason });
                    this.$bvToast.toast("반려가 완료되었습니다.", { variant: "success", title: "반려 완료" });
                    await this.loadPendingApprovals();
                } catch (error) {
                    console.error("반려 처리 실패:", error);
                    this.$bvToast.toast("반려 처리에 실패했습니다.", { variant: "danger", title: "반려 실패" });
                }
            }
        },

        openCreateRequestModal() {
            this.showCreateRequestModal = true;
        },

        async createRequest() {
            try {
                await carbonApi.workflow.createApproval(this.createForm);
                this.$bvToast.toast("승인 요청이 생성되었습니다.", { variant: "success", title: "요청 생성" });
                this.showCreateRequestModal = false;
                await this.loadMyRequests();
            } catch (error) {
                console.error("요청 생성 실패:", error);
                this.$bvToast.toast("요청 생성에 실패했습니다.", { variant: "danger", title: "생성 실패" });
            }
        },

        formatDateTime(date) {
            if (!date) return "-";
            return new Date(date).toLocaleString("ko-KR", {
                year: "2-digit",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        getTargetTypeLabel(type) {
            return CarbonCodes.targetType.getLabel(type) || type;
        },

        getTargetIcon(type) {
            const icons = {
                activity_data: "fas fa-chart-line",
                inventory: "fas fa-clipboard-list",
                report: "fas fa-file-alt",
            };
            return icons[type] || "fas fa-file";
        },

        getTargetClass(type) {
            return `target-${type?.replace("_", "-")}`;
        },

        getStatusLabel(status) {
            return CarbonCodes.transStat.getLabel(status) || status;
        },

        getStatusIcon(status) {
            const icons = {
                pending: "fas fa-clock",
                approved: "fas fa-check-circle",
                rejected: "fas fa-times-circle",
            };
            return icons[status] || "fas fa-question-circle";
        },

        getStatusClass(status) {
            return `status-${status}`;
        },
    },
};
</script>

<style scoped>
/* 메인 레이아웃 */
.workflow-compact {
    padding: 16px;
}

/* 섹션 블록 */
.section-block {
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #ffffff;
}

.section-header-compact {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px 14px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
}

.section-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

/* WorkflowManager 전용: .btn-add는 아웃라인 스타일 */
.btn-add {
    background: #ffffff;
    color: #52b788;
    border-color: #52b788;
}

.btn-add:hover {
    background: #52b788;
    color: #ffffff;
}

/* 테이블 */
/* table-compact 스타일은 전역 _carbon-tables.scss 적용 */

/* 태그 */
.tag-small {
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.tag-small.target-activity-data {
    background: #eff6ff;
    color: #2563eb;
}

.tag-small.target-inventory {
    background: #fffbeb;
    color: #f59e0b;
}

.tag-small.target-report {
    background: #f0f9ff;
    color: #0284c7;
}

.target-name-text {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
}

.status-tag {
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.status-tag.status-pending {
    background: #fffbeb;
    color: #f59e0b;
}

.status-tag.status-approved {
    background: #f0fdf4;
    color: #16a34a;
}

.status-tag.status-rejected {
    background: #fef2f2;
    color: #dc2626;
}

/* 액션 버튼 */
/* action-buttons 스타일은 전역 _carbon-tables.scss 적용 */

.btn-mini {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 4px;
}

.btn-approve {
    background: #f0fdf4;
    color: #16a34a;
}

.btn-approve:hover {
    background: #16a34a;
    color: #ffffff;
}

.btn-reject {
    background: #fef2f2;
    color: #dc2626;
}

.btn-reject:hover {
    background: #dc2626;
    color: #ffffff;
}

/* 모달 */
.modal-form-compact {
    padding: 10px;
}

.form-group-compact {
    margin-bottom: 14px;
}

.form-group-compact label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
}

.modal-footer-compact {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px;
    border-top: 1px solid #e2e8f0;
}

/* 유틸리티 */
.text-center {
    text-align: center;
}

.empty {
    padding: 30px;
    color: #94a3b8;
}
</style>
